import { Pencil, Trash2 } from "lucide-react";
import {
  Form,
  useDelete,
  useGetIdentity,
  useNotify,
  useResourceContext,
  useTranslate,
  useUpdate,
} from "ra-core";
import { useState } from "react";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { Button } from "@/components/ui/button";

import { formatDate } from "../misc/formatDate";
import { Markdown } from "../misc/Markdown";
import { useGetSalesName } from "../sales/useGetSalesName";
import type { ContactNote, SponsoringNote } from "../types";
import { NoteInputs } from "./NoteInputs";
import { toNoteDate } from "./noteModel";

/** One note: author, date, text, and inline edit/delete. */
export const Note = ({ note }: { note: SponsoringNote | ContactNote }) => {
  const [isEditing, setEditing] = useState(false);
  const resource = useResourceContext();
  const notify = useNotify();
  const translate = useTranslate();
  const { identity } = useGetIdentity();
  const isCurrentUser = note.sales_id === identity?.id;
  const salesName = useGetSalesName(note.sales_id, {
    enabled: !isCurrentUser,
  });

  const [update, { isPending }] = useUpdate();
  const [deleteNote] = useDelete(resource, undefined, {
    mutationMode: "undoable",
    onSuccess: () => {
      notify("resources.notes.deleted", { type: "info", undoable: true });
    },
  });

  const handleDelete = () => {
    deleteNote(resource, { id: note.id, previousData: note });
  };

  const handleNoteUpdate: SubmitHandler<FieldValues> = (values) => {
    update(
      resource,
      {
        id: note.id,
        data: { ...values, date: toNoteDate(values.date, note.date) },
        previousData: note,
      },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <div className="group py-1">
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <span className="font-medium text-foreground">
          {isCurrentUser ? translate("resources.notes.me") : salesName}
        </span>
        <span aria-hidden="true">·</span>
        <span>{formatDate(note.date)}</span>
        <div className="flex-1" />
        <div className="flex opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
          <Button
            variant="ghost"
            size="sm"
            className="h-auto p-1 cursor-pointer"
            onClick={() => setEditing(!isEditing)}
          >
            <Pencil className="w-4 h-4" />
            <span className="sr-only">
              {translate("resources.notes.action.edit")}
            </span>
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-auto p-1 cursor-pointer"
            onClick={handleDelete}
          >
            <Trash2 className="w-4 h-4" />
            <span className="sr-only">
              {translate("resources.notes.action.delete")}
            </span>
          </Button>
        </div>
      </div>

      {isEditing ? (
        <Form onSubmit={handleNoteUpdate} record={note} className="mt-2">
          <NoteInputs />
          <div className="flex justify-end gap-2 mt-2">
            <Button
              variant="ghost"
              type="button"
              className="cursor-pointer"
              onClick={() => setEditing(false)}
            >
              {translate("ra.action.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              className="cursor-pointer"
            >
              {translate("resources.notes.action.update")}
            </Button>
          </div>
        </Form>
      ) : (
        note.text && (
          <div className="pt-1 text-sm max-w-200">
            <Markdown>{note.text}</Markdown>
          </div>
        )
      )}
    </div>
  );
};
